import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Box,
  Typography,
  IconButton,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import {
  commonStyles,
  getStatusColor,
  getSeverityColor,
  getTypeColor,
  getEmailColor,
  getProcessingColor,
  layoutStyles,
} from '../styles';

const NotificationModal = ({ 
  open, 
  onClose, 
  notification 
}) => {
  if (!notification) return null;

  const formatDuration = (startTime, endTime) => {
    const start = dayjs(startTime);
    const end = dayjs(endTime);
    const duration = end.diff(start, 'minutes');

    if (duration < 60) {
      return `${duration} minutes`;
    } else if (duration < 1440) {
      const hours = Math.floor(duration / 60);
      const minutes = duration % 60;
      return `${hours}h ${minutes}m`;
    } else {
      const days = Math.floor(duration / 1440);
      const hours = Math.floor((duration % 1440) / 60);
      return `${days}d ${hours}h`;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: commonStyles.modalPaper,
      }}
    >
      <DialogTitle sx={commonStyles.modalTitle}>
        <Typography variant="h6">Notification Details</Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={commonStyles.modalCloseButton}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={layoutStyles.modalContent}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={layoutStyles.detailBox}>
              <Typography variant="subtitle2" color="textSecondary">
                Plant Name
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {notification.plantName}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={layoutStyles.detailBox}>
              <Typography variant="subtitle2" color="textSecondary">
                Plant ID
              </Typography>
              <Typography variant="body1">
                {notification.plantId}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={layoutStyles.detailBox}>
              <Typography variant="subtitle2" color="textSecondary">
                Type
              </Typography>
              <Chip
                label={notification.type.replace(/_/g, ' ')}
                sx={{
                  ...commonStyles.chip,
                  backgroundColor: getTypeColor(notification.type),
                }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={layoutStyles.detailBox}>
              <Typography variant="subtitle2" color="textSecondary">
                Escalation Level
              </Typography>
              <Typography variant="body1">
                Level {notification.escalationLevel}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={layoutStyles.detailBox}>
              <Typography variant="subtitle2" color="textSecondary">
                Message
              </Typography>
              <Typography variant="body1">
                {notification.message}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={layoutStyles.detailBox}>
              <Typography variant="subtitle2" color="textSecondary">
                Status
              </Typography>
              <Chip
                label={
                  notification.status.charAt(0).toUpperCase() +
                  notification.status.slice(1)
                }
                sx={{
                  ...commonStyles.chip,
                  backgroundColor: getStatusColor(notification.status),
                }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={layoutStyles.detailBox}>
              <Typography variant="subtitle2" color="textSecondary">
                Severity
              </Typography>
              <Chip
                label={
                  notification.severity.charAt(0).toUpperCase() +
                  notification.severity.slice(1)
                }
                sx={{
                  ...commonStyles.chip,
                  backgroundColor: getSeverityColor(notification.severity),
                }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={layoutStyles.detailBox}>
              <Typography variant="subtitle2" color="textSecondary">
                Created Date & Time
              </Typography>
              <Typography variant="body1">
                {dayjs(notification.created_at).format('DD-MM-YYYY HH:mm:ss')}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={layoutStyles.detailBox}>
              <Typography variant="subtitle2" color="textSecondary">
                Last Data Time
              </Typography>
              <Typography variant="body1">
                {dayjs(notification.lastDataTime).format('DD-MM-YYYY HH:mm:ss')}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={layoutStyles.detailBox}>
              <Typography variant="subtitle2" color="textSecondary">
                Email Status
              </Typography>
              <div>
                <Chip
                  label={notification.emailSent ? 'Email Sent' : 'Email Not Sent'}
                  sx={{
                    ...commonStyles.chip,
                    backgroundColor: getEmailColor(notification.emailSent),
                    ...layoutStyles.emailStatusContainer,
                  }}
                />
                {notification.emailSentTime && (
                  <Typography variant="body2">
                    Sent at:{' '}
                    {dayjs(notification.emailSentTime).format('DD-MM-YYYY HH:mm:ss')}
                  </Typography>
                )}
              </div>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={layoutStyles.detailBox}>
              <Typography variant="subtitle2" color="textSecondary">
                Processing Status
              </Typography>
              <Chip
                label={notification.processed ? 'Processed' : 'Not Processed'}
                sx={{
                  ...commonStyles.chip,
                  backgroundColor: getProcessingColor(notification.processed),
                }}
              />
            </Box>
          </Grid>
          
          {notification.resolved && (
            <>
              <Grid item xs={12} md={6}>
                <Box sx={layoutStyles.detailBox}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Resolution Time
                  </Typography>
                  <Typography variant="body1">
                    {dayjs(notification.resolutionTime).format('DD-MM-YYYY HH:mm:ss')}
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={layoutStyles.detailBox}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Downtime Duration
                  </Typography>
                  <Typography variant="body1">
                    {formatDuration(notification.lastDataTime, notification.resolutionTime)}
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={layoutStyles.detailBox}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Resolution Details
                  </Typography>
                  <Typography variant="body1">
                    {notification.resolutionDetails}
                  </Typography>
                </Box>
              </Grid>
            </>
          )}
          
          <Grid item xs={12}>
            <Box sx={layoutStyles.detailBox}>
              <Typography variant="subtitle2" color="textSecondary">
                Escalation Time
              </Typography>
              <Typography variant="body1">
                {dayjs(notification.escalationTime).format('DD-MM-YYYY HH:mm:ss')}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions sx={layoutStyles.modalActions}>
        <Button onClick={onClose} sx={commonStyles.closeButton}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotificationModal;